/**
 * useLoginState — l'écran `/login` sur le design system : ce qui est tapé, l'étape, et les trois
 * appels (demander un code, le vérifier, le mot de passe).
 *
 * Portage littéral de l'ancien `auth/LoginScreen.js`, mêmes appels, mêmes corps, mêmes règles :
 *  - un numéro envoie tout de suite un SMS ; un e-mail mène au mot de passe, et le code par e-mail
 *    ne part que sur demande (`data/auth/loginSteps`) ;
 *  - « Mot de passe oublié ? » envoie un code par e-mail, puis offre « Choisir un nouveau mot de
 *    passe » (l'écran `reset-password`) ;
 *  - le renvoi attend `resend_after` secondes (60 à défaut), à la seconde ;
 *  - un code complet (tapé, collé, proposé par le système, lu dans le SMS par Chrome Android)
 *    connecte sans autre geste, sauf tant qu'une erreur est affichée ; un code faux vide le champ ;
 *  - le mot de passe part après le jeton CSRF sur le web, directement sur un téléphone ;
 *  - après une connexion, `data/auth/loginSuccess` : la mesure `login.succeeded`, puis « Qui utilise
 *    Masteur ? » ou l'accueil du compte.
 *
 * Les refus du serveur sont gardés dans le réducteur (pas dans la tranche de l'api, que
 * `useApiMutation` rend au repos) et effacés aux mêmes gestes que l'ancien écran.
 */
import { useEffect, useReducer, useRef } from "react"
import { Keyboard } from "react-native"
import { useDispatch, useSelector } from "react-redux"
import * as Device from "expo-device"
import { useNavigation } from "@react-navigation/native"

import {
    hasStoredAccounts as deviceHasStoredAccounts,
    useLandIfAlreadyLoggedIn,
    useSessionFromAnotherTab,
} from "../../../../../../data/auth/crossTabSession"
import { codeSentTo } from "../../../../../../data/auth/loginCodeSent"
import {
    BACK_ACTIONS,
    backAction,
    initialLoginStep,
    LOGIN_MODES,
    LOGIN_STEPS,
    START_ACTIONS,
    startAction,
} from "../../../../../../data/auth/loginSteps"
import {
    continueAfterLogin,
    PASSWORD_LOGIN_METHOD,
    trackLoginSucceeded,
} from "../../../../../../data/auth/loginSuccess"
import { apiIdle } from "../../../../../../data/redux/actions/api"
import { switchAccount } from "../../../../../../data/redux/actions/authTokens"
import { authService } from "../../../../../../data/services/authService"
import { authErrorText } from "../../../hooks/apiErrorMessage"
import { useApiMutation } from "../../../hooks/useApiMutation"
import { IS_WEB, openExternalUrl } from "../../../hooks/openExternalUrl"
import { useBackOr } from "../../../hooks/useBackOr"
import { type UILoginMode } from "../../../types"
import {
    isCodeDead,
    type LoginCodeSentTo,
    type LoginErrors,
    loginPageContent,
    type LoginUiState,
    NO_LOGIN_ERRORS,
} from "../../../../view-models/auth/Login/loginViewModel"
import { EMAIL_INVALID, isValidEmail } from "../../../../view-models/auth/Password/passwordFormViewModel"

export const LOGIN_CODE_LENGTH = 6

/** Repli d'un refus sans message lisible (un 500, une coupure) : jamais un écran muet. */
const LOGIN_FAILED = "La connexion n'a pas abouti. Réessayez dans un instant."

const errorText = (error: unknown): string | undefined =>
    error == null ? undefined : authErrorText(error, LOGIN_FAILED)
const TERMS_URL = "https://masteur.com/cgu"

/** `LOGIN_MODES` (`data/auth/loginSteps`, en JS), typé. */
const MODES = LOGIN_MODES as { EMAIL: UILoginMode; PHONE: UILoginMode }

/** Un clavier matériel passe outre le pavé numérique (`onChangeAdditional` du champ numéro). */
export const sanitizePhone = (value: string) => value.replace(/[^\d+ .()-]/g, "")

export type LoginUiAction =
    | { type: "SET_EMAIL"; email: string }
    | { type: "SET_PHONE"; phone: string }
    | { type: "SET_PASSWORD"; password: string }
    | { type: "SET_CODE"; code: string }
    | { type: "SWITCH_MODE" }
    | { type: "GO_TO_STEP"; step: LoginUiState["step"]; lockEmail?: boolean; resetMode?: boolean }
    | { type: "EMAIL_INVALID" }
    | { type: "FORGOT_PASSWORD"; forgot: boolean }
    | { type: "CODE_SENT"; sentTo: LoginCodeSentTo; resendIn: number }
    | { type: "LEAVE_CODE" }
    | { type: "TICK" }
    | { type: "FAILED"; call: keyof LoginErrors; error: unknown }
    | { type: "CLEAR_ERROR"; call: keyof LoginErrors }

export function initialLoginUi(routeName: string | undefined, prefillEmail: string | undefined): LoginUiState {
    return {
        step: initialLoginStep(routeName, prefillEmail),
        mode: MODES.EMAIL,
        email: prefillEmail || "",
        phone: "",
        password: "",
        code: "",
        emailLocked: !!prefillEmail,
        emailError: null,
        sentTo: null,
        resendIn: 0,
        resent: false,
        forgotPassword: false,
        errors: NO_LOGIN_ERRORS,
    }
}

export function loginUiReducer(state: LoginUiState, action: LoginUiAction): LoginUiState {
    switch (action.type) {
        case "SET_EMAIL":
            return { ...state, email: action.email, emailError: null }
        case "SET_PHONE":
            // Retaper le numéro efface le refus de l'envoi (`onChangeAdditional`).
            return { ...state, phone: action.phone, errors: { ...state.errors, request: null } }
        case "SET_PASSWORD":
            return { ...state, password: action.password, errors: { ...state.errors, password: null } }
        case "SET_CODE": {
            // Un code mort ne se retape pas : son message reste (`onChangeAdditional` du champ).
            const errors = isCodeDead(state.errors.verify) ? state.errors : { ...state.errors, verify: null }
            return { ...state, code: action.code, errors }
        }
        case "SWITCH_MODE":
            return {
                ...state,
                mode: state.mode === MODES.EMAIL ? MODES.PHONE : MODES.EMAIL,
                emailError: null,
                errors: { ...state.errors, request: null },
            }
        case "GO_TO_STEP":
            // `goToStep` : les refus de l'envoi et du mot de passe partent avec l'étape.
            return {
                ...state,
                step: action.step,
                mode: action.resetMode ? MODES.EMAIL : state.mode,
                emailLocked: action.lockEmail ?? state.emailLocked,
                emailError: null,
                errors: { ...state.errors, request: null, password: null },
            }
        case "EMAIL_INVALID":
            return { ...state, emailError: EMAIL_INVALID }
        case "FORGOT_PASSWORD":
            return { ...state, forgotPassword: action.forgot }
        case "CODE_SENT":
            return {
                ...state,
                resent: state.sentTo != null,
                sentTo: action.sentTo,
                resendIn: action.resendIn,
                code: "",
                errors: { ...state.errors, request: null },
            }
        case "LEAVE_CODE":
            // `changeIdentifier` : retour à l'étape qui a demandé le code.
            return {
                ...state,
                sentTo: null,
                resent: false,
                forgotPassword: false,
                errors: { ...state.errors, request: null, verify: null },
            }
        case "TICK":
            return { ...state, resendIn: Math.max(0, state.resendIn - 1) }
        case "FAILED":
            // Un code faux se retape, il ne se corrige pas : le champ se vide, le message reste.
            return {
                ...state,
                code: action.call === "verify" ? "" : state.code,
                errors: { ...state.errors, [action.call]: action.error },
            }
        case "CLEAR_ERROR":
            return { ...state, errors: { ...state.errors, [action.call]: null } }
        default:
            return state
    }
}

/**
 * Chrome sur Android lit le code dans le SMS (WebOTP). Rend le code, ou rien quand le navigateur ne
 * sait pas ou que l'étape est quittée — `readSmsCodeOnWeb` de l'ancien écran.
 */
function readSmsCodeOnWeb(signal: AbortSignal): Promise<string | undefined> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const w = typeof window === "undefined" ? undefined : (window as any)
    if (!IS_WEB || !w?.OTPCredential) return new Promise(() => {})
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (navigator.credentials as any)
        .get({ otp: { transport: ["sms"] }, signal })
        .then((otp: { code?: string } | null) => otp?.code)
        .catch(() => new Promise(() => {}))
}

type LoginNavigation = {
    canGoBack: () => boolean
    goBack: () => void
    replace: (name: string, params?: Record<string, unknown>) => void
    push: (name: string, params?: Record<string, unknown>) => void
    getState: () => { routes?: { name: string }[]; index?: number } | undefined
}

export type LoginRoute = {
    name?: string
    params?: { prefillEmail?: string; passwordIndication?: unknown }
}

export function useLoginState(route: LoginRoute | undefined) {
    const navigation = useNavigation<LoginNavigation>()
    const dispatch = useDispatch()
    const { prefillEmail, passwordIndication } = route?.params ?? {}
    // Une valeur d'adresse web arrive en chaîne : comme l'ancien écran, toute valeur l'allume.
    const fastTrackArrival = !!passwordIndication

    useLandIfAlreadyLoggedIn(navigation)
    const connectedElsewhere = useSessionFromAnotherTab(navigation)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const hasStoredAccounts = useSelector((state: any) => deviceHasStoredAccounts(state?.data))

    const [ui, dispatchUi] = useReducer(loginUiReducer, undefined, () => initialLoginUi(route?.name, prefillEmail))

    const land = (method: string, successData: unknown) => {
        trackLoginSucceeded(method, successData, { designSystem: true })
        continueAfterLogin(successData, {
            navigation,
            switchAccount: (tokenData: unknown) => dispatch(switchAccount(tokenData)),
        })
    }

    // L'identifiant d'un envoi est figé à l'envoi : un renvoi garde le sien (`codeSentTo`).
    const requestedIdentifierRef = useRef<string | null>(null)
    const request = useApiMutation({
        service: authService.requestLoginCode,
        onSuccess: (data) =>
            dispatchUi({
                type: "CODE_SENT",
                sentTo: codeSentTo(requestedIdentifierRef.current, data),
                resendIn: (data as { resend_after?: number } | null)?.resend_after ?? 60,
            }),
        onError: (_message, error) => dispatchUi({ type: "FAILED", call: "request", error }),
    })
    const verify = useApiMutation({
        service: authService.verifyLoginCode,
        // La méthode vient du serveur (`email_code`, `sms_code`).
        onSuccess: (data) => land((data as { method?: string } | null)?.method as string, data),
        onError: (_message, error) => dispatchUi({ type: "FAILED", call: "verify", error }),
    })
    const password = useApiMutation({
        service: authService.getSanctumToken,
        // La session web a besoin de son cookie CSRF d'abord.
        prerequest: IS_WEB,
        onSuccess: (data) => land(PASSWORD_LOGIN_METHOD, data),
        onError: (_message, error) => dispatchUi({ type: "FAILED", call: "password", error }),
    })

    // Un reste d'une visite précédente ne se rejoue pas (le montage de l'ancien écran).
    useEffect(() => {
        dispatch(apiIdle(authService.requestLoginCode) as never)
        dispatch(apiIdle(authService.verifyLoginCode) as never)
        dispatch(apiIdle(authService.getSanctumToken) as never)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // Le compte à rebours du renvoi, à la seconde.
    useEffect(() => {
        if (ui.resendIn <= 0) return
        const timer = setTimeout(() => dispatchUi({ type: "TICK" }), 1000)
        return () => clearTimeout(timer)
    }, [ui.resendIn])

    // Chrome sur Android : le code du SMS remplit le champ.
    useEffect(() => {
        if (ui.sentTo?.channel !== "sms") return
        const controller = new AbortController()
        readSmsCodeOnWeb(controller.signal).then((smsCode) => {
            if (smsCode) dispatchUi({ type: "SET_CODE", code: smsCode })
        })
        return () => controller.abort()
    }, [ui.sentTo])

    const checkEmail = () => {
        if (isValidEmail(ui.email.trim())) return true
        dispatchUi({ type: "EMAIL_INVALID" })
        return false
    }

    const requestCode = (value: string) => {
        if (request.requesting || !value) return
        Keyboard.dismiss()
        dispatchUi({ type: "CLEAR_ERROR", call: "verify" })
        requestedIdentifierRef.current = value
        request.start({}, { identifier: value })
    }

    const submitCode = (value: string) => {
        if (verify.requesting || !ui.sentTo || value.length !== LOGIN_CODE_LENGTH) return
        verify.start({}, { identifier: ui.sentTo.identifier, code: value, device_name: Device.deviceName ?? "WEB" })
    }

    // Un code complet connecte sans autre geste, sauf tant qu'une erreur est affichée.
    useEffect(() => {
        if (ui.code.length === LOGIN_CODE_LENGTH && ui.errors.verify == null) submitCode(ui.code)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ui.code])

    const backToStart = () => dispatchUi({ type: "GO_TO_STEP", step: LOGIN_STEPS.START, resetMode: true })
    const leaveScreen = useBackOr("menu")

    const email = ui.email.trim()
    const phone = ui.phone.trim()

    const handlers = {
        onSwitchMode: () => dispatchUi({ type: "SWITCH_MODE" }),
        onEmailChange: (value: string) => dispatchUi({ type: "SET_EMAIL", email: value }),
        onPhoneChange: (value: string) => dispatchUi({ type: "SET_PHONE", phone: sanitizePhone(value) }),
        onContinue: () => {
            if (startAction(ui.mode) === START_ACTIONS.SEND_SMS) {
                requestCode(phone)
                return
            }
            if (!checkEmail()) return
            Keyboard.dismiss()
            dispatchUi({ type: "GO_TO_STEP", step: LOGIN_STEPS.PASSWORD, lockEmail: true })
        },
        // Comme l'ancien lien des conditions : un onglet sur le web, le navigateur intégré ailleurs.
        onOpenTerms: () => openExternalUrl(TERMS_URL),
        onBecomeTutor: () => navigation.push("register"),
        onEditEmail: backToStart,
        onPasswordChange: (value: string) => dispatchUi({ type: "SET_PASSWORD", password: value }),
        onSubmitPassword: () => {
            if (password.requesting || !ui.password || !checkEmail()) return
            Keyboard.dismiss()
            password.start(
                {},
                { email, password: ui.password, device_name: Device.deviceName ?? (IS_WEB ? "WEB" : "emu") }
            )
        },
        onForgotPassword: () => {
            if (!checkEmail()) return
            dispatchUi({ type: "FORGOT_PASSWORD", forgot: true })
            requestCode(email)
        },
        onRequestEmailCode: () => {
            if (!checkEmail()) return
            dispatchUi({ type: "FORGOT_PASSWORD", forgot: false })
            requestCode(email)
        },
        onCodeChange: (value: string) => dispatchUi({ type: "SET_CODE", code: value }),
        onSubmitCode: () => submitCode(ui.code),
        onResend: () => {
            if (ui.sentTo) requestCode(ui.sentTo.identifier)
        },
        onChangeIdentifier: () => {
            const channel = ui.sentTo?.channel
            dispatchUi({ type: "LEAVE_CODE" })
            if (channel === "email") backToStart()
        },
        onUsePassword: () => dispatchUi({ type: "LEAVE_CODE" }),
        onChooseNewPassword: () => navigation.push("reset-password"),
    }

    const content = loginPageContent(
        ui,
        {
            requesting: request.requesting,
            verifying: verify.requesting,
            loggingIn: password.requesting,
            connectedElsewhere,
            passwordIndication: fastTrackArrival,
            isWeb: IS_WEB,
            errorTexts: {
                request: errorText(ui.errors.request),
                verify: errorText(ui.errors.verify),
                password: errorText(ui.errors.password),
            },
        },
        handlers
    )

    // La flèche remonte d'une étape avant de quitter l'écran (`backAction`). L'arrivée du fast-track
    // n'en a pas. Quitter l'écran vers l'accueil (rien dessous, ou `menu` — une adresse `/login`
    // ouverte directement le pose sous elle) ne mène ailleurs que s'il reste des comptes sur
    // l'appareil : sinon l'accueil renvoie ici, la connexion EST l'accueil, sans flèche.
    const action = backAction({ codeSent: !!ui.sentTo, step: ui.step })
    const { routes = [], index = 0 } = navigation.getState() ?? {}
    const leavesToWelcome = index === 0 || routes[index - 1]?.name === "menu"
    const noArrow =
        connectedElsewhere ||
        (fastTrackArrival && ui.step === LOGIN_STEPS.PASSWORD && !ui.sentTo) ||
        (action === BACK_ACTIONS.LEAVE_SCREEN && leavesToWelcome && !hasStoredAccounts)
    const onBack = noArrow
        ? undefined
        : () => {
              // Du code à l'étape qui l'a demandé : le mot de passe pour un code par e-mail.
              if (action === BACK_ACTIONS.LEAVE_CODE) dispatchUi({ type: "LEAVE_CODE" })
              else if (action === BACK_ACTIONS.TO_START) backToStart()
              else leaveScreen()
          }

    return { content, onBack }
}
